import { Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type {
  OrganizationDetailInvitedOwnersItem,
  OrganizationDetailOwnersItem,
} from '@nucleus-commerce/api-client/platform';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';

import { useResendOwnerInvite } from '../hooks/use-resend-owner-invite';

import { FixOwnerEmailDialog } from './fix-owner-email-dialog';

export interface OwnersCardProps {
  organizationId: string;
  owners: readonly OrganizationDetailOwnersItem[];
  invitedOwners: readonly OrganizationDetailInvitedOwnersItem[];
  staffCount: number;
  /** Owners and admins may resend the invitation or correct its address. */
  canManage: boolean;
  className?: string;
}

/**
 * Active members holding the owner role, then owners who have not accepted their invitation
 * yet, with the size of the whole staff in the header.
 */
export function OwnersCard({
  organizationId,
  owners,
  invitedOwners,
  staffCount,
  canManage,
  className,
}: OwnersCardProps) {
  const { t } = useTranslation('organizations');
  const resend = useResendOwnerInvite(organizationId);
  const empty = owners.length === 0 && invitedOwners.length === 0;
  return (
    <Card className={className} data-tour="platform.organizations.owners">
      <CardHeader>
        <CardTitle>{t('detail.owners.title')}</CardTitle>
        <CardDescription>{t('detail.owners.staffCount', { count: staffCount })}</CardDescription>
      </CardHeader>
      <CardContent>
        {empty ? (
          <p className="text-muted-foreground text-sm">{t('detail.owners.empty')}</p>
        ) : (
          <ul
            className="divide-border flex flex-col divide-y text-sm"
            aria-label={t('detail.owners.title')}
          >
            {owners.map((owner) => (
              <li key={owner.id} className="flex flex-col gap-0.5 py-2 first:pt-0 last:pb-0">
                <span className="font-medium">{owner.name}</span>
                <a
                  href={`mailto:${owner.email}`}
                  className="text-muted-foreground truncate hover:underline"
                >
                  {owner.email}
                </a>
              </li>
            ))}
            {invitedOwners.map((owner) => (
              <li key={owner.id} className="flex flex-col gap-1.5 py-2 first:pt-0 last:pb-0">
                <span className="flex items-center gap-2">
                  <span className="font-medium">{owner.name}</span>
                  <Badge variant="outline" className="font-normal">
                    {t('detail.owners.invited')}
                  </Badge>
                </span>
                <a
                  href={`mailto:${owner.email}`}
                  className="text-muted-foreground truncate hover:underline"
                >
                  {owner.email}
                </a>
                {canManage ? (
                  <span className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={resend.isPending}
                      onClick={() => resend.mutate()}
                    >
                      <Send aria-hidden />
                      {resend.isPending ? t('detail.owners.resending') : t('detail.owners.resend')}
                    </Button>
                    <FixOwnerEmailDialog organizationId={organizationId} owner={owner} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
